import { format, parseISO, parse, addDays } from 'date-fns';
import _ from 'lodash';

export const MM_DD_YYYY = 'MM-dd-yyyy';
export const ENGLISH_FORMAT = 'dd MMMM yyyy';

export function convertToDateObject(dateStr) {
    if (!dateStr) return null;
    // not using new Date(dateStr) as it fails on safari 15.x or below.
    // refer: Prop#317688 for details
    return parseISO(dateStr);
}

export function formatDateObject(dateObj, dateFormat = MM_DD_YYYY) {
    return format(dateObj, dateFormat);
}

export function formatDateString(dateStr, dateFormat = MM_DD_YYYY) {
    if (_.isEmpty(dateStr)) {
        return '-';
    }
    const dateObj = convertToDateObject(dateStr);
    return formatDateObject(dateObj, dateFormat);
}

/**
 * Convert a date string in English format to a Date object
 * @param {string} dateStr - Date string in English format (e.g., "28 November 2025")
 * @returns {Date|null} - Date object or null if parsing fails
 */
export function parseEnglishFormatDate(dateStr) {
    if (!dateStr) return null;

    try {
        const dateObj = parse(dateStr, ENGLISH_FORMAT, new Date());
        if (Number.isNaN(dateObj.getTime())) {
            return null;
        }
        return dateObj;
    } catch {
        return null;
    }
}

/**
 * Add days to a date string
 * @param {string} dateStr - Date string to add days to (supports both ISO format and English format)
 * @param {number} daysToAdd - Number of days to add
 * @param {string} dateFormat - Date format to return
 * @returns {string} - Date string with days added
 */
export function addDaysToDate(dateStr, daysToAdd, dateFormat = MM_DD_YYYY) {
    if (!dateStr) return '-';

    // Try parsing as English format first, then fall back to ISO format
    let dateObj = parseEnglishFormatDate(dateStr);
    if (!dateObj) {
        dateObj = convertToDateObject(dateStr);
    }

    if (!dateObj || Number.isNaN(dateObj.getTime())) {
        return '-';
    }
    const newDate = addDays(dateObj, daysToAdd);
    return formatDateObject(newDate, dateFormat);
}

/**
 * Check if a given date is within N days from today (inclusive)
 * @param {string} dateStr - Date string to check (supports both ISO format and English format)
 * @param {number} days - Number of days to check within
 * @returns {boolean} - True if date is within N days from today (inclusive), false otherwise
 */
export function isWithinNDaysFromToday(dateStr, days) {
    if (!dateStr || days < 0) return false;

    // Try parsing as English format first, then fall back to ISO format
    let dateObj = parseEnglishFormatDate(dateStr);
    if (!dateObj) {
        dateObj = convertToDateObject(dateStr);
    }

    if (!dateObj || Number.isNaN(dateObj.getTime())) {
        return false;
    }

    // Normalize both dates to midnight for accurate day wise comparison
    const normalizedDate = new Date(dateObj);
    normalizedDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Calculate difference in days
    const diffInMs = today - normalizedDate;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    // Return true if difference is between 0 and days (inclusive)
    return diffInDays >= 0 && diffInDays <= days;
}
